import { useState, useRef } from "react"
import {
  Send,
  Zap,
  Upload,
  Check,
  Landmark,
  Building2,
  CreditCard,
  Search,
  Bell,
  Download,
  Receipt,
  Calendar,
  Repeat,
  Radio,
  FileText,
  ShieldCheck,
} from "lucide-react"
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog"
import { Button } from "@/components/ui/Button"
import { Avatar } from "@/components/ui/Avatar"
import { cn } from "@/utils/Functions"
import { notifyDemoAction } from "@/utils/demoNotifications"
import type { DeliveryItem, DeliveryItemDetailFields, PaymentAutomation } from "@/types/delivery-manager.types"
import { isPayment, isArtifact } from "@/types/delivery-manager.types"
import { formatCents, shortDate, automationConfig, frequencyLabels, formatFileSize } from "@/constants/delivery-manager.constants"
import { useDeliveryManagerData } from "@/hooks/delivery-manager/useDeliveryManagerData"
import type { ConnectedAccount } from "@/types/delivery-manager.types"
import { Input } from "@/components/ui/Input"
// ═══════════════════════════════════════════════════════════════════════
//  Mock vault data
// ═══════════════════════════════════════════════════════════════════════

interface VaultItem {
  id: string
  name: string
  fileType: string
  updatedAt: string
}

const vaultItems: VaultItem[] = [
  { id: "vault-1", name: "Sony Recording Agreement (Executed)", fileType: "PDF", updatedAt: "2026-02-14" },
  { id: "vault-2", name: "Nike Ambassador Agreement", fileType: "PDF", updatedAt: "2026-01-10" },
  { id: "vault-3", name: "Netflix Sync License Draft", fileType: "DOCX", updatedAt: "2026-02-28" },
  { id: "vault-4", name: "Publishing Registration Certificate", fileType: "PDF", updatedAt: "2026-02-20" },
  { id: "vault-5", name: "Producer Agreement — Alex Rivera", fileType: "PDF", updatedAt: "2026-01-25" },
  { id: "vault-6", name: "Master Recording Stems (Tracks 1-4)", fileType: "ZIP", updatedAt: "2026-03-01" },
]

// ═══════════════════════════════════════════════════════════════════════
//  Shared helpers
// ═══════════════════════════════════════════════════════════════════════

const accountIcons: Record<ConnectedAccount["type"], typeof Landmark> = {
  checking: Landmark,
  savings: Building2,
  card: CreditCard,
}

interface FlowDialogProps {
  item: DeliveryItem & DeliveryItemDetailFields
  open: boolean
  onOpenChange: (open: boolean) => void
}

function LoadingSpinner() {
  return <div className="size-8 border-2 border-muted-foreground/20 border-t-foreground rounded-full animate-spin" />
}

// ═══════════════════════════════════════════════════════════════════════
//  Action Panel — contextual actions based on item kind / status
// ═══════════════════════════════════════════════════════════════════════

interface ActionPanelProps {
  item: DeliveryItem & DeliveryItemDetailFields
}

export function ActionPanel({ item }: ActionPanelProps) {
  const [sendPaymentOpen, setSendPaymentOpen] = useState(false)
  const [configAutoOpen, setConfigAutoOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [vaultOpen, setVaultOpen] = useState(false)
  const [requestOpen, setRequestOpen] = useState(false)

  const isCompleted = item.status === "completed"
  const isPaymentItem = item.kind === "payment"
  const isSend = item.direction === "send"

  // Completed items — single contextual action
  if (isCompleted) {
    return (
      <ActionSection>
        <ActionCard
          label={isPaymentItem ? "View Receipt" : "Download"}
          description={isPaymentItem ? "View payment confirmation" : "Download delivered file"}
          icon={isPaymentItem ? Receipt : Download}
          iconBg="bg-emerald-500/10"
          iconColor="text-emerald-400"
          onClick={() => notifyDemoAction(isPaymentItem ? "Viewing receipt" : "Downloading file")}
        />
      </ActionSection>
    )
  }

  // Active payment — Send/Request + Automation
  if (isPaymentItem) {
    const hasAutomation = isPayment(item) && !!item.automation
    return (
      <ActionSection>
        <div className="grid grid-cols-2 gap-2">
          <ActionCard
            label={isSend ? "Send Payment" : "Request Payment"}
            description={isSend ? "Pay now via connected account" : "Send a payment reminder"}
            icon={isSend ? Send : Bell}
            iconBg={isSend ? "bg-blue-500/10" : "bg-violet-500/10"}
            iconColor={isSend ? "text-blue-400" : "text-violet-400"}
            onClick={() => (isSend ? setSendPaymentOpen(true) : setRequestOpen(true))}
          />
          <ActionCard
            label={hasAutomation ? "Edit Automation" : "Add Automation"}
            description={
              hasAutomation && isPayment(item) && item.automation
                ? `${automationConfig[item.automation.type].label} active`
                : "Set up a trigger rule"
            }
            icon={Zap}
            iconBg="bg-amber-500/10"
            iconColor="text-amber-400"
            onClick={() => setConfigAutoOpen(true)}
          />
        </div>
        <SendPaymentDialog item={item} open={sendPaymentOpen} onOpenChange={setSendPaymentOpen} />
        <ConfigureAutomationDialog item={item} open={configAutoOpen} onOpenChange={setConfigAutoOpen} />
        <RequestDialog item={item} open={requestOpen} onOpenChange={setRequestOpen} kind="payment" />
      </ActionSection>
    )
  }

  // Active artifact — Upload/Vault (send) or Request (receive)
  return (
    <ActionSection>
      {isSend ? (
        <div className="grid grid-cols-2 gap-2">
          <ActionCard
            label="Upload File"
            description="Upload from your device"
            icon={Upload}
            iconBg="bg-blue-500/10"
            iconColor="text-blue-400"
            onClick={() => setUploadOpen(true)}
          />
          <ActionCard
            label="From Vault"
            description="Select an existing document"
            icon={FileText}
            iconBg="bg-violet-500/10"
            iconColor="text-violet-400"
            onClick={() => setVaultOpen(true)}
          />
        </div>
      ) : (
        <ActionCard
          label="Request Delivery"
          description="Send a reminder to the counterparty"
          icon={Bell}
          iconBg="bg-violet-500/10"
          iconColor="text-violet-400"
          onClick={() => setRequestOpen(true)}
        />
      )}
      <UploadArtifactDialog item={item} open={uploadOpen} onOpenChange={setUploadOpen} />
      <VaultSelectDialog item={item} open={vaultOpen} onOpenChange={setVaultOpen} />
      <RequestDialog item={item} open={requestOpen} onOpenChange={setRequestOpen} kind="artifact" />
    </ActionSection>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Action section / card primitives
// ═══════════════════════════════════════════════════════════════════════

function ActionSection({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-t border-border">
      <div className="px-5 pt-4 pb-1 flex items-center gap-2">
        <div className="size-5 rounded flex items-center justify-center bg-muted shrink-0">
          <Zap className="size-3 text-muted-foreground" />
        </div>
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Actions</span>
      </div>
      <div className="px-5 pb-4">{children}</div>
    </div>
  )
}

interface ActionCardProps {
  label: string
  description: string
  icon: typeof Send
  iconBg: string
  iconColor: string
  onClick: () => void
}

function ActionCard({ label, description, icon: Icon, iconBg, iconColor, onClick }: ActionCardProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-3 hover:bg-muted/60 transition-colors text-left w-full"
    >
      <div className={cn("size-9 rounded-lg flex items-center justify-center shrink-0", iconBg)}>
        <Icon className={cn("size-4", iconColor)} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-[11px] text-muted-foreground leading-tight">{description}</p>
      </div>
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Send Payment Dialog
// ═══════════════════════════════════════════════════════════════════════

function SendPaymentDialog({ item, open, onOpenChange }: FlowDialogProps) {
  const { connectedAccounts: mockConnectedAccounts } = useDeliveryManagerData()
  const [step, setStep] = useState<"review" | "processing" | "success">("review")
  const [selectedAccount, setSelectedAccount] = useState(mockConnectedAccounts[0]?.id ?? "")
  const [memo, setMemo] = useState("")

  if (!isPayment(item)) return null

  const handleClose = (v: boolean) => {
    if (!v) setTimeout(() => { setStep("review"); setMemo("") }, 300)
    onOpenChange(v)
  }

  const handleSend = () => {
    setStep("processing")
    setTimeout(() => setStep("success"), 1500)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPopup className="sm:max-w-md">
        {step === "review" && (
          <>
            <DialogHeader className="p-6 pb-0">
              <DialogTitle>Send Payment</DialogTitle>
              <DialogDescription>Review and confirm payment details</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              {/* Amount */}
              <div className="rounded-xl bg-muted/40 border border-border p-4 text-center">
                <p className="text-xs text-muted-foreground">Amount</p>
                <p className="text-3xl font-bold text-foreground mt-1">{formatCents(item.amountCents)}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{item.currency}</p>
              </div>

              {/* From → To */}
              <div className="flex items-center gap-3">
                <PartyBubble party={item.from} label="From" />
                <Send className="size-4 text-muted-foreground shrink-0" />
                <PartyBubble party={item.to} label="To" />
              </div>

              {/* Payment method */}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Payment Method</p>
                <div className="space-y-1.5">
                  {mockConnectedAccounts.map((acct) => {
                    const Icon = accountIcons[acct.type]
                    return (
                      <button
                        key={acct.id}
                        onClick={() => setSelectedAccount(acct.id)}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left",
                          selectedAccount === acct.id
                            ? "border-blue-400/50 bg-blue-400/5"
                            : "border-border hover:bg-muted/30",
                        )}
                      >
                        <Icon className="size-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{acct.label}</p>
                          <p className="text-[11px] text-muted-foreground">{acct.institution} ••{acct.lastFour}</p>
                        </div>
                        <span className="text-xs text-muted-foreground">{formatCents(acct.balanceCents)}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Memo */}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Memo (optional)</p>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="Add a note..."
                  className="w-full h-16 px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:border-ring"
                />
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
              <Button className="flex-1 gap-1.5" onClick={handleSend}>
                <Send className="size-3.5" />
                Send {formatCents(item.amountCents)}
              </Button>
            </div>
          </>
        )}

        {step === "processing" && (
          <div className="p-12 flex flex-col items-center justify-center">
            <LoadingSpinner />
            <p className="text-sm text-muted-foreground mt-4">Processing payment...</p>
          </div>
        )}

        {step === "success" && (
          <div className="p-8 flex flex-col items-center text-center">
            <div className="size-14 rounded-full bg-emerald-400/10 flex items-center justify-center mb-4">
              <Check className="size-7 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Payment Sent</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {formatCents(item.amountCents)} sent to {item.to.name}
            </p>
            <p className="text-xs text-muted-foreground/60 mt-3 font-mono">
              Ref: WR-2026-{String(Math.floor(Math.random() * 9999)).padStart(4, "0")}
            </p>
            <Button className="mt-6 w-full" onClick={() => handleClose(false)}>Done</Button>
          </div>
        )}
      </DialogPopup>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Configure Automation Dialog
// ═══════════════════════════════════════════════════════════════════════

const triggerTypes: { type: PaymentAutomation["type"]; label: string; icon: typeof Zap }[] = [
  { type: "on_delivery", label: "On Delivery", icon: Zap },
  { type: "on_date", label: "Scheduled Date", icon: Calendar },
  { type: "recurring", label: "Recurring", icon: Repeat },
  { type: "streaming", label: "Streaming", icon: Radio },
  { type: "requires_approval", label: "Requires Approval", icon: ShieldCheck },
]

function ConfigureAutomationDialog({ item, open, onOpenChange }: FlowDialogProps) {
  const { items: mockDeliveryItems, parties: PARTIES } = useDeliveryManagerData()
  const existing = isPayment(item) ? item.automation : undefined
  const [triggerType, setTriggerType] = useState<PaymentAutomation["type"]>(existing?.type ?? "on_delivery")

  if (!isPayment(item)) return null

  const handleClose = (v: boolean) => {
    if (!v) setTimeout(() => setTriggerType(existing?.type ?? "on_delivery"), 300)
    onOpenChange(v)
  }

  const handleSave = () => {
    notifyDemoAction("Automation configured")
    handleClose(false)
  }

  const artifacts = mockDeliveryItems.filter(isArtifact)

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPopup className="sm:max-w-md">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>{existing ? "Edit" : "Configure"} Automation</DialogTitle>
          <DialogDescription>Set up a trigger rule for this payment</DialogDescription>
        </DialogHeader>
        <div className="p-6 space-y-4">
          {/* Trigger type selector */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Trigger Type</p>
            <div className="grid grid-cols-2 gap-1.5">
              {triggerTypes.map((tt) => {
                const selected = triggerType === tt.type
                return (
                  <button
                    key={tt.type}
                    onClick={() => setTriggerType(tt.type)}
                    className={cn(
                      "flex items-center gap-2 p-2.5 rounded-lg border transition-colors text-left",
                      selected
                        ? "border-blue-400/50 bg-blue-400/5"
                        : "border-border hover:bg-muted/30",
                    )}
                  >
                    <tt.icon className={cn("size-3.5 shrink-0", selected ? "text-blue-400" : "text-muted-foreground")} />
                    <p className={cn("text-xs font-medium", selected ? "text-foreground" : "text-muted-foreground")}>{tt.label}</p>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Type-specific configuration */}
          <div className="rounded-lg border border-border p-3 space-y-3">
            {triggerType === "on_delivery" && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Linked Deliverable</p>
                <div className="space-y-1">
                  {artifacts.slice(0, 4).map((a) => (
                    <label key={a.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/30 cursor-pointer text-xs">
                      <input
                        type="radio"
                        name="deliverable"
                        defaultChecked={existing?.type === "on_delivery" && existing.deliverableId === a.id}
                        className="accent-blue-400"
                      />
                      <span className="text-foreground">{a.title}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {triggerType === "on_date" && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Trigger Date</p>
                <input
                  type="date"
                  defaultValue={existing?.type === "on_date" ? existing.date : ""}
                  className="w-full h-8 px-3 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-ring"
                />
              </div>
            )}

            {triggerType === "recurring" && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Frequency</p>
                <div className="flex gap-1.5 flex-wrap">
                  {(["weekly", "monthly", "quarterly", "yearly"] as const).map((freq) => (
                    <button
                      key={freq}
                      className={cn(
                        "px-3 py-1.5 rounded-md text-xs border transition-colors",
                        existing?.type === "recurring" && existing.frequency === freq
                          ? "border-blue-400/50 bg-blue-400/5 text-foreground"
                          : "border-border text-muted-foreground hover:bg-muted/30",
                      )}
                    >
                      {frequencyLabels[freq]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {triggerType === "streaming" && (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">Revenue Source</p>
                  <input
                    type="text"
                    defaultValue={existing?.type === "streaming" ? existing.source : ""}
                    placeholder="e.g. Spotify / Apple Music"
                    className="w-full h-8 px-3 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring"
                  />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">Unit Label (optional)</p>
                  <input
                    type="text"
                    defaultValue={existing?.type === "streaming" ? (existing.unitLabel ?? "") : ""}
                    placeholder="e.g. per stream"
                    className="w-full h-8 px-3 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring"
                  />
                </div>
              </div>
            )}

            {triggerType === "requires_approval" && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Approver</p>
                <div className="space-y-1">
                  {[PARTIES.sony, PARTIES.alex, PARTIES.nike, PARTIES.morgan].map((party) => (
                    <label key={party.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/30 cursor-pointer text-xs">
                      <input
                        type="radio"
                        name="approver"
                        defaultChecked={existing?.type === "requires_approval" && existing.approverId === party.id}
                        className="accent-blue-400"
                      />
                      <Avatar fallback={party.initials} className="size-5" />
                      <span className="text-foreground">{party.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
          <Button className="flex-1 gap-1.5" onClick={handleSave}>
            <Check className="size-3.5" />
            Save
          </Button>
        </div>
      </DialogPopup>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Upload Artifact Dialog
// ═══════════════════════════════════════════════════════════════════════

function UploadArtifactDialog({ item, open, onOpenChange }: FlowDialogProps) {
  const [step, setStep] = useState<"select" | "uploading" | "success">("select")
  const [file, setFile] = useState<File | null>(null)
  const [versionNotes, setVersionNotes] = useState("")
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const hasVersions = isArtifact(item) && !!item.versions && item.versions.length > 0

  const handleClose = (v: boolean) => {
    if (!v) setTimeout(() => { setStep("select"); setFile(null); setVersionNotes(""); setDragOver(false) }, 300)
    onOpenChange(v)
  }

  const handleFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) setFile(files[0])
  }

  const handleUpload = () => {
    setStep("uploading")
    setTimeout(() => setStep("success"), 1500)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    handleFileSelect(e.dataTransfer.files)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPopup className="sm:max-w-md">
        {step === "select" && (
          <>
            <DialogHeader className="p-6 pb-0">
              <DialogTitle>Upload File</DialogTitle>
              <DialogDescription>
                {hasVersions ? `Upload a new version of "${item.title}"` : `Upload file for "${item.title}"`}
              </DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 cursor-pointer transition-colors",
                  dragOver
                    ? "border-blue-400 bg-blue-400/5"
                    : file
                      ? "border-emerald-400/50 bg-emerald-400/5"
                      : "border-border hover:border-muted-foreground/30 hover:bg-muted/20",
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
                {file ? (
                  <>
                    <FileText className="size-8 text-emerald-400 mb-2" />
                    <p className="text-sm font-medium text-foreground">{file.name}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{formatFileSize(file.size)}</p>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null) }}
                      className="text-[11px] text-red-400 hover:text-red-300 mt-2 transition-colors"
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <Upload className="size-8 text-muted-foreground/40 mb-2" />
                    <p className="text-sm text-muted-foreground">Drop file here or click to browse</p>
                    <p className="text-[11px] text-muted-foreground/60 mt-1">Any file type accepted</p>
                  </>
                )}
              </div>

              {/* Version notes */}
              {hasVersions && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">
                    Version Notes <span className="text-muted-foreground/50">(v{(item.versions?.length ?? 0) + 1})</span>
                  </p>
                  <textarea
                    value={versionNotes}
                    onChange={(e) => setVersionNotes(e.target.value)}
                    placeholder="Describe what changed..."
                    className="w-full h-16 px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:border-ring"
                  />
                </div>
              )}
            </div>
            <div className="px-6 pb-6 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
              <Button className="flex-1 gap-1.5" disabled={!file} onClick={handleUpload}>
                <Upload className="size-3.5" />
                Upload
              </Button>
            </div>
          </>
        )}

        {step === "uploading" && (
          <div className="p-12 flex flex-col items-center justify-center">
            <LoadingSpinner />
            <p className="text-sm text-muted-foreground mt-4">Uploading {file?.name}...</p>
          </div>
        )}

        {step === "success" && (
          <div className="p-8 flex flex-col items-center text-center">
            <div className="size-14 rounded-full bg-emerald-400/10 flex items-center justify-center mb-4">
              <Check className="size-7 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">File Uploaded</h3>
            <p className="text-sm text-muted-foreground mt-1">{file?.name}</p>
            {hasVersions && (
              <p className="text-xs text-muted-foreground/60 mt-1">Version {(item.versions?.length ?? 0) + 1}</p>
            )}
            <Button className="mt-6 w-full" onClick={() => handleClose(false)}>Done</Button>
          </div>
        )}
      </DialogPopup>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Vault Select Dialog
// ═══════════════════════════════════════════════════════════════════════

function VaultSelectDialog({ item, open, onOpenChange }: FlowDialogProps) {
  void item
  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const handleClose = (v: boolean) => {
    if (!v) setTimeout(() => { setSearch(""); setSelectedId(null) }, 300)
    onOpenChange(v)
  }

  const filtered = search
    ? vaultItems.filter((v) => v.name.toLowerCase().includes(search.toLowerCase()))
    : vaultItems

  const handleAttach = () => {
    const selected = vaultItems.find((v) => v.id === selectedId)
    notifyDemoAction(`Attached "${selected?.name}"`)
    handleClose(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPopup className="sm:max-w-md">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Select from Vault</DialogTitle>
          <DialogDescription>Choose an existing document to attach</DialogDescription>
        </DialogHeader>
        <div className="p-6 space-y-3">
          {/* Search */}
          <div className="relative">  
            <Input
              size="sm"
              startIcon={<Search className="size-2 text-muted-foreground pointer-events-none" />}
              type="search"
              placeholder="Search vault..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              aria-label="Search vault"
            />
          </div>

          {/* Items */}
          <div className="max-h-[240px] overflow-y-auto rounded-lg border border-border divide-y divide-border">
            {filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No matching documents</p>
            ) : (
              filtered.map((vi) => (
                <button
                  key={vi.id}
                  onClick={() => setSelectedId(vi.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 transition-colors text-left",
                    selectedId === vi.id ? "bg-blue-400/5" : "hover:bg-muted/30",
                  )}
                >
                  <FileText className={cn("size-4 shrink-0", selectedId === vi.id ? "text-blue-400" : "text-muted-foreground")} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{vi.name}</p>
                    <p className="text-[10px] text-muted-foreground">{vi.fileType} · Updated {shortDate(vi.updatedAt)}</p>
                  </div>
                  {selectedId === vi.id && <Check className="size-4 text-blue-400 shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
          <Button className="flex-1 gap-1.5" disabled={!selectedId} onClick={handleAttach}>
            <Check className="size-3.5" />
            Attach
          </Button>
        </div>
      </DialogPopup>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Request Dialog (payment reminder / delivery request)
// ═══════════════════════════════════════════════════════════════════════

function RequestDialog({ item, open, onOpenChange, kind }: FlowDialogProps & { kind: "payment" | "artifact" }) {
  const [message, setMessage] = useState("")
  const counterparty = item.direction === "receive" ? item.from : item.to

  const handleClose = (v: boolean) => {
    if (!v) setTimeout(() => setMessage(""), 300)
    onOpenChange(v)
  }

  const handleSend = () => {
    notifyDemoAction(kind === "payment" ? "Payment reminder sent" : "Delivery request sent")
    handleClose(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPopup className="sm:max-w-sm">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>{kind === "payment" ? "Request Payment" : "Request Delivery"}</DialogTitle>
          <DialogDescription>Send a reminder to {counterparty.name}</DialogDescription>
        </DialogHeader>
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 rounded-lg bg-muted/30 border border-border p-3">
            <Avatar fallback={counterparty.initials} className="size-8" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{counterparty.name}</p>
              <p className="text-[11px] text-muted-foreground">{item.title}</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Message (optional)</p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={kind === "payment" ? "Friendly payment reminder..." : "Requesting delivery of..."}
              className="w-full h-20 px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:border-ring"
            />
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
          <Button className="flex-1 gap-1.5" onClick={handleSend}>
            <Send className="size-3.5" />
            Send
          </Button>
        </div>
      </DialogPopup>
    </Dialog>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Shared sub-components
// ═══════════════════════════════════════════════════════════════════════

function PartyBubble({ party, label }: { party: { name: string; initials: string }; label: string }) {
  return (
    <div className="flex-1 rounded-lg bg-muted/30 border border-border p-3">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <div className="flex items-center gap-2 mt-1">
        <Avatar fallback={party.initials} className="size-5" />
        <span className="text-sm font-medium text-foreground truncate">{party.name}</span>
      </div>
    </div>
  )
}
